import type { ReactNode } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Icon,
  type IconName,
  Image,
  ProgressPie,
  Text,
} from "@salestrak/ui";
import { useMediaObjectUrl } from "#app/lib/hooks/use-note-media";
import { fileIconName, hasThumbnail, mediaKind } from "#app/lib/media-kind";
import type { Omittable } from "@salestrak/ui";

// Shared thumbnail-tile primitives for permission-gated photos (note
// attachments, photos-type custom fields). Bytes resolve through the
// authenticated content route via useMediaObjectUrl.

/** Note-attachment tiles (composer cards, posted notes). */
export const MEDIA_TILE_SIZE = 80;
export const FIELD_MEDIA_TILE_SIZE = 24;

export const REMOVE_BUTTON_OVERHANG = -12;

export type MediaTileSize = number | "fill";

// Progress-pie edge lengths for the two tile scales (px — the particle takes
// a number, not a named size).
const PIE_SIZE_COMPACT = 16;
const PIE_SIZE = 24;

function tileBox(size: MediaTileSize) {
  return size === "fill"
    ? ({ className: "aspect-square", fill: "width" } as const)
    : { height: size, width: size };
}

type CornerRung = "compact" | "overhang" | "inset";

function cornerRung(size: MediaTileSize): CornerRung {
  if (size === "fill") return "inset";
  return size <= 48 ? "compact" : "overhang";
}

const SCRIM_EDGE: Record<Exclude<CornerRung, "overhang">, number> = {
  compact: 16,
  inset: 28,
};
const SCRIM_OFFSET: Record<Exclude<CornerRung, "overhang">, number> = {
  compact: 1,
  inset: 4,
};

// The tile frame every variant shares. `position="relative"` is load-bearing:
// the play badge and corner affordances are absolutely positioned, and a
// standalone tile (posted note, media tab) has no other positioned ancestor.
const TILE_FRAME = {
  position: "relative",
  rounded: "md",
  overflow: "hidden",
  border: true,
  background: "muted",
  align: "center",
  justify: "center",
} as const;

// Hover-revealed corner affordance: visible on hover, on keyboard focus, and
// always where hover does not exist (coarse pointers).
const REVEAL_ON_HOVER =
  "opacity-0 transition-opacity focus-within:opacity-100 group-hover/tile:opacity-100 pointer-coarse:opacity-100";

/** Centered play affordance overlaid on a video poster tile. The white icon is
 * theme-independent so it reads over any poster (same choice as the scrim
 * buttons). The single home for this overlay — never hand-roll it per tile. */
export function VideoPlayBadge({ size = "lg" }: { size?: "sm" | "lg" }) {
  return (
    <Box
      pointerEvents="none" position="absolute"
      inset={0}
      align="center"
      justify="center"
    >
      <Icon name="play-circle" size={size} className="text-white" />
    </Box>
  );
}

/** Resolved tile (posted notes, field display, media tab). Optionally clickable
 * — e.g. opening the fullscreen viewer at this item. Tiles are always small
 * (≤80px), so they resolve the thumb variant. Pass `contentType` for non-image
 * media: documents (no thumbnail) render a file icon, videos get a play badge.
 * Without it the tile is image-only (the legacy photos-field / composer use). */
export function ResolvedMediaTile({
  photoId,
  contentType,
  size = MEDIA_TILE_SIZE,
  onClick,
  label = "View attachment",
  cover = false,
}: Omittable<{
  photoId: string;
  contentType?: string;
  size?: MediaTileSize;
  onClick?: () => void;
  label?: string;
  cover?: boolean;
}>) {
  const kind = contentType ? mediaKind(contentType) : "image";
  const hasThumb = contentType ? hasThumbnail(contentType) : true;
  // "" short-circuits the fetch (the hook guards it) for thumbless documents.
  const uri = useMediaObjectUrl(hasThumb ? photoId : "", "thumb");
  const small = size !== "fill" && size <= 48;
  const content = (
    <>
      {hasThumb && uri ? (
        <Image src={uri} fill fit="cover" alt="Photo" />
      ) : kind === "file" && contentType ? (
        <Icon
          name={fileIconName(contentType)}
          size={small ? "sm" : "md"}
          color="muted"
        />
      ) : null}
      {kind === "video" ? <VideoPlayBadge size={small ? "sm" : "lg"} /> : null}
    </>
  );
  return onClick ? (
    <Box
      as="button"
      {...TILE_FRAME}
      {...tileBox(size)}
      cursor="pointer"
      onClick={onClick}
      aria-label={label}
      pressFeedback
    >
      {content}
    </Box>
  ) : cover ? (
    <Box {...TILE_FRAME} border={false} rounded={undefined} fill="size">
      {content}
    </Box>
  ) : (
    <Box {...TILE_FRAME} {...tileBox(size)}>
      {content}
    </Box>
  );
}

/**
 * Corner affordance shared by the removable + uploading tiles, revealed on
 * hover of the enclosing `group/tile` and always visible on touch. The rung
 * (`cornerRung`) places it: a solid button straddling the corner, or a scrim
 * circle inside it. `compactIcon` defaults to `icon` — pass it when the scrim's
 * glyph differs (the remove tile shows a trash button but an × scrim).
 */
function CornerActionButton({
  icon,
  compactIcon = icon,
  label,
  onClick,
  rung,
}: Omittable<{
  icon: IconName;
  compactIcon?: IconName;
  label: string;
  onClick: () => void;
  rung: CornerRung;
}>) {
  const offset = rung === "overhang" ? REMOVE_BUTTON_OVERHANG : SCRIM_OFFSET[rung];
  return (
    <Box
      position="absolute"
      top={offset}
      right={offset}
      // Paints over the neighboring tile it straddles into.
      zIndex={1}
      className={REVEAL_ON_HOVER}
    >
      {rung !== "overhang" ? (
        <Box
          as="button"
          width={SCRIM_EDGE[rung]}
          height={SCRIM_EDGE[rung]}
          rounded="full"
          // Scrim over an arbitrary photo — theme-independent, like the
          // dialog overlay's black scrim.
          className="bg-black/60"
          align="center"
          justify="center"
          onClick={onClick}
          aria-label={label}
        >
          <Icon
            name={compactIcon}
            size={rung === "inset" ? "sm" : "xs"}
            className="text-white"
          />
        </Box>
      ) : (
        <Button size="icon-xs" onClick={onClick} aria-label={label}>
          <Icon name={icon} size="xs" />
        </Button>
      )}
    </Box>
  );
}

/**
 * Editable tile: resolved thumbnail with a remove affordance — hover-revealed
 * with a pointer, always visible on touch. Large tiles get the trash button
 * straddling the top-right corner (give the row overhang headroom); small and
 * grid tiles get a scrim button inside the corner instead. With `onOpen` the
 * thumbnail itself is pressable (the viewer, at this photo).
 */
export function RemovableMediaTile({
  photoId,
  onRemove,
  onOpen,
  openLabel,
  size = MEDIA_TILE_SIZE,
}: Omittable<{
  photoId: string;
  onRemove: (photoId: string) => void;
  onOpen?: () => void;
  openLabel?: string;
  size?: MediaTileSize;
}>) {
  return (
    <Box group="tile" position="relative" {...tileBox(size)}>
      <ResolvedMediaTile label={openLabel} onClick={onOpen} photoId={photoId} size={size} />
      <CornerActionButton
        icon="trash"
        compactIcon="x"
        label="Remove photo"
        onClick={() => onRemove(photoId)}
        rung={cornerRung(size)}
      />
    </Box>
  );
}

export function SelectableMediaTile({
  photoId,
  contentType,
  label,
  selected,
  onSelectedChange,
  onOpen,
  caption,
  menu,
  editing = false,
}: Omittable<{
  photoId: string;
  contentType?: string;
  label: string;
  selected: boolean;
  onSelectedChange?: (selected: boolean) => void;
  onOpen: () => void;
  caption?: ReactNode;
  menu?: ReactNode;
  editing?: boolean;
}>) {
  return (
    <Card
      aria-label={label}
      direction="column"
      fill="size"
      group="tile"
      position="relative"
      radius="lg"
      className={selected ? "ring-2 ring-brand ring-offset-2 ring-offset-background" : undefined}
      {...(editing
        ? {}
        : {
            cursor: "pointer" as const,
            hoverBackground: true,
            onClick: onOpen,
            pressFeedback: true,
            role: "button",
          })}
    >
      <Box fill="width" shrink={false} className="aspect-[4/3]">
        <ResolvedMediaTile contentType={contentType} cover photoId={photoId} />
      </Box>
      {/* A press on a corner must stop at its control: a portalled menu's events still bubble the React tree to the card. */}
      {onSelectedChange ? (
        <Box
          position="absolute"
          top={SCRIM_OFFSET.inset}
          left={SCRIM_OFFSET.inset}
          zIndex={1}
          className={selected ? undefined : REVEAL_ON_HOVER}
          onClick={(event) => event.stopPropagation()}
        >
          <Checkbox
            aria-label="Select file"
            checked={selected}
            onCheckedChange={(checked) => onSelectedChange(checked === true)}
          />
        </Box>
      ) : null}
      {menu ? (
        <Box
          position="absolute"
          top={SCRIM_OFFSET.inset}
          right={SCRIM_OFFSET.inset}
          zIndex={1}
          className={REVEAL_ON_HOVER}
          onClick={(event) => event.stopPropagation()}
        >
          {menu}
        </Box>
      ) : null}
      {caption ? (
        <Box direction="column" padding="sm" minWidth={0}>
          {caption}
        </Box>
      ) : null}
    </Card>
  );
}

/**
 * In-flight upload tile: the local preview (dimmed under a scrim) with a
 * determinate progress pie centered over it. `uri` is null until the file
 * finishes client-side processing (preview not yet available — just the
 * scrim + pie). `progress` is 0–100. The pie is white over the dark scrim so
 * it reads against any photo. With `onCancel`, a × button (hover-revealed on
 * pointer, always visible on touch) aborts the upload — same corner affordance
 * as RemovableMediaTile's remove button.
 */
export function UploadingMediaTile({
  uri,
  progress,
  onCancel,
  size = MEDIA_TILE_SIZE,
}: Omittable<{
  uri: string | null;
  progress: number;
  onCancel?: () => void;
  size?: MediaTileSize;
}>) {
  const compact = size !== "fill" && size <= 48;
  return (
    <Box
      group="tile" {...TILE_FRAME}
      {...tileBox(size)}
      aria-label={`Uploading photo, ${Math.round(progress)} percent`}
    >
      {uri ? (
        <Image src={uri} fill fit="cover" alt="Uploading photo" />
      ) : null}
      <Box
        position="absolute"
        inset={0}
        align="center"
        justify="center"
        // Scrim over an arbitrary photo — theme-independent, matching the
        // remove-button scrim — so the white pie always reads.
        className="bg-black/40"
      >
        <ProgressPie
          percentage={progress}
          size={compact ? PIE_SIZE_COMPACT : PIE_SIZE}
          color="#ffffff"
        />
      </Box>
      {onCancel ? (
        <CornerActionButton
          icon="x"
          label="Cancel upload"
          onClick={onCancel}
          rung={cornerRung(size)}
        />
      ) : null}
    </Box>
  );
}

export function OverflowMediaTile({
  photoId,
  uri,
  count,
  onClick,
  size = MEDIA_TILE_SIZE,
}: Omittable<{
  photoId?: string;
  uri?: string | null;
  count: number;
  onClick?: () => void;
  size?: MediaTileSize;
}>) {
  const thumb = useMediaObjectUrl(photoId ?? "", "thumb");
  const src = photoId ? thumb : (uri ?? null);
  return (
    <Box
      as="button"
      {...TILE_FRAME}
      {...tileBox(size)}
      aria-label={`View ${count} more photos`}
      cursor={onClick ? "pointer" : undefined}
      disabled={!onClick}
      onClick={onClick}
      pressFeedback
    >
      {src ? <Image src={src} fill fit="cover" alt="" /> : null}
      <Box
        position="absolute"
        inset={0}
        align="center"
        justify="center"
        pointerEvents="none"
        // The remove scrim's rung, a step deeper: the count is the tile's
        // content here, and it must read over the brightest sky.
        // Deeper scrim: the count must read over the brightest sky.
        className="bg-black/55"
      >
        <Text size="lg" weight="semibold" className="text-white">
          +{count}
        </Text>
      </Box>
    </Box>
  );
}

/** Where a photo was deleted after the fact (a submitted form's answer): a
 *  clean box that says so, so the answer still shows something was there. */
export function DeletedMediaTile({ size = MEDIA_TILE_SIZE }: Omittable<{ size?: MediaTileSize }>) {
  return (
    <Box rounded="md" border align="center" justify="center" padding="xs" {...tileBox(size)}>
      <Text size="xs" variant="muted" align="center">
        Photo deleted
      </Text>
    </Box>
  );
}

/** Dashed "add photo" tile, sized to match the thumbnails. `icon` swaps the
 * glyph for alternate intents (e.g. "camera" for a capture-first tile). */
export function AddMediaTile({
  onClick,
  disabled,
  size = MEDIA_TILE_SIZE,
  icon = "plus",
  label = "Add photo",
}: Omittable<{
  onClick: () => void;
  disabled?: boolean;
  size?: number;
  icon?: IconName;
  label?: string;
}>) {
  return (
    <Box
      borderStyle="dashed" as="button"
      width={size}
      height={size}
      rounded="md"
      border
      align="center"
      justify="center"
      hoverBackground
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      className="disabled:pointer-events-none disabled:opacity-50"
    >
      <Icon name={icon} size={size <= 48 ? "xs" : "md"} color="muted" />
    </Box>
  );
}
